import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  getMinimumNumberOfPhotos,
  getMaximumNumberOfPhotos,
  getInitialPageId,
  getLayoutAssetUrl,
  TEMPLATE_LAYOUT,
  getNumberOfCoverPhotos,
  getPageOptionIds,
  getLayoutType,
} from 'editor-utils/template';
import generateClassicData from '../../generator/generateClassicData';
import useEditorItemContext from '../../context/editorItemContext';
import useEditorContext from '../../context/editorContext';

import PhotoWell from '../../components/photoWell';
import DesignSelect from '../components/designSelect';
import EditorLayout from '../components/layout';
import { Typography } from '../../components/base';
import { Infobox } from '../../components/rich';
import OptionedContinueButton from '../components/optionedContinueButton';

const TITLE = (numberOfUploadedPhotos = 0, minimumMorePhotos = 1) => {
  if (numberOfUploadedPhotos === 0) {
    if (minimumMorePhotos === 1) {
      return 'CHOOSE YOUR PHOTO';
    }
    return 'CHOOSE YOUR PHOTOS';
  }

  if (numberOfUploadedPhotos === 1) {
    if (minimumMorePhotos === 0) {
      return 'YOUR PHOTO UPLOADED!';
    }
    return '1 PHOTO UPLOADED';
  }

  return `${numberOfUploadedPhotos} PHOTOS UPLOADED!`;
};

const DESCRIPTION = (
  productId,
  numberOfUploadedPhotos,
  minimumMorePhotos = 0,
  maximumMorePhotos = 0,
) => {
  const howManyPhotosLabel = (min, max) => {
    if (min === 1) {
      if (max === 1) {
        return '1 photo';
      }
      return '1 photo (or more)';
    }
    if (max > min) {
      if (min <= 0) {
        return 'more photos';
      }
      return `${min} photos (or more)`;
    }
    return `${min} photos`;
  };

  if (numberOfUploadedPhotos === 0) {
    return `Upload ${howManyPhotosLabel(
      minimumMorePhotos,
      maximumMorePhotos,
    )} in JPG format, and we will take care of the rest! You can select multiple files when picking photos.`;
  }

  if (minimumMorePhotos <= 0 && maximumMorePhotos > 0) {
    return 'Upload more photos if you’d like, or continue on to preview your prints.';
  }

  if (minimumMorePhotos > 0) {
    return `Upload at least ${minimumMorePhotos} more photos in JPG format, and we will take care of the rest! You can select multiple files when picking photos.`;
  }

  return 'Click on the Preview button on the top of this page to preview your creation.';
};

const PhotoSource = props => {
  const editorContext = useEditorContext();
  const { item, product, template } = useEditorItemContext();

  const [initialPageId, setInitialPageId] = useState(getInitialPageId(template, item.pages));

  const numberOfCoverPhotos = getNumberOfCoverPhotos(template, initialPageId);
  const minimumNumberOfPhotos = getMinimumNumberOfPhotos(template, initialPageId);
  const maximumNumberOfPhotos = getMaximumNumberOfPhotos(template, initialPageId);

  const minimumRequiredNumberOfPhotos = minimumNumberOfPhotos - item.photos.length;
  const maximumRequiredNumberOfPhotos = maximumNumberOfPhotos - item.photos.length;

  const isPreviewEnabled = item.photos.length >= minimumNumberOfPhotos;

  const handleOnPreviewClick = async optionIds => {
    const numberOfDamagedPhotos = item.photos.filter(photo => photo.alive === -1).length;
    if (numberOfDamagedPhotos > 0) {
      await props.onSuccess(() => ({
        error: 'Please, remove damaged photos.',
      }));
      return;
    }

    if (item.photos.length < minimumNumberOfPhotos) {
      const minimumMorePhotos = minimumNumberOfPhotos - item.photos.length;

      await props.onSuccess(() => ({
        error: `Please, upload at least ${minimumMorePhotos} more ${
          minimumMorePhotos === 1 ? 'photo' : 'photos'
        }.`,
      }));
      return;
    }

    await props.onSuccess(() =>
      generateClassicData(editorContext.accessToken, template, item.photos, initialPageId),
    );
  };

  const sideToolbars = [];
  if (getLayoutType(template) === TEMPLATE_LAYOUT.PAGE) {
    sideToolbars.push(
      <DesignSelect
        key="design_page"
        title="PICK A DESIGN"
        active={initialPageId}
        onChange={setInitialPageId}
        items={template.pages.map(page => ({
          key: page.id,
          title: page.caption,
          icon: getLayoutAssetUrl(template, page.id, {
            media: '@2x',
            suffix: initialPageId === page.id && '-active',
          }),
        }))}
        className="mb-3"
      />,
    );
  }

  if (template.cover && template.cover.length > 1) {
    sideToolbars.push(
      <DesignSelect
        key="design_cover"
        title="PICK A COVER DESIGN"
        active={initialPageId}
        onChange={setInitialPageId}
        items={template.cover.map(page => ({
          key: page.id,
          title: page.caption,
          icon: getLayoutAssetUrl(template, page.id),
        }))}
        className="mb-3"
      />,
    );
  }

  return (
    <EditorLayout
      actionButton={
        <OptionedContinueButton
          disabled={!isPreviewEnabled}
          onClick={handleOnPreviewClick}
          checkErrors={false}
          options={getPageOptionIds(template)}
        >
          Preview
        </OptionedContinueButton>
      }
      toolbars={sideToolbars}
      primaryChildren={props.children}
    >
      <Infobox mb={2}>
        <Typography.Heading size={3} mb={1}>
          {TITLE(item.photos.length, minimumNumberOfPhotos)}
        </Typography.Heading>
        <Typography.Paragraph>
          {DESCRIPTION(
            product.id,
            item.photos.length,
            minimumRequiredNumberOfPhotos,
            maximumRequiredNumberOfPhotos,
          )}
        </Typography.Paragraph>
      </Infobox>

      <PhotoWell
        id={item.id}
        numberOfCoverPhotos={numberOfCoverPhotos}
        minimumNumberOfPhotos={minimumNumberOfPhotos}
        maximumNumberOfPhotos={maximumNumberOfPhotos}
      />
    </EditorLayout>
  );
};

PhotoSource.propTypes = {
  onSuccess: PropTypes.func,
  children: PropTypes.node,
};

export default PhotoSource;
